import { createBrowserRouter, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import AppLayout from "./layouts/AppLayout";
import Home from "./pages/Home";
import NotFoundPage from "./pages/NotFoundPage";
import { OAuthCallback } from "./pages/auth/OAuthCallback";
import { OAuthErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import { UnifiedLoadingScreen } from "./components/UnifiedLoadingScreen";
import { useAuth } from "./contexts/AuthContext";

const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const BookPage = lazy(() => import("./pages/BookPage"));
const ReaderChatExperiencePage = lazy(() => import("./pages/ReaderChatExperiencePage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const EditBookPage = lazy(() => import("./pages/EditBookPage"));
const BookGraphPage = lazy(() => import("./pages/BookGraphPage"));
const NewBookIngestPage = lazy(() => import("./pages/NewBookIngestPage"));
const AdminBookListPage = lazy(() => import("./pages/AdminBookListPage"));
const Health = lazy(() => import("./pages/Health"));
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const SignupPage = lazy(() => import("./pages/auth/SignupPage"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const CheckYourEmailPage = lazy(() => import("./pages/auth/CheckYourEmailPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const PasswordResetSuccessPage = lazy(() => import("./pages/auth/PasswordResetSuccessPage"));
const EmailVerificationPage = lazy(() => import("./pages/auth/EmailVerificationPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const EulaPage = lazy(() => import("./pages/EulaPage"));

const LoadingFallback = () => {
  const location = useLocation();
  const { isBootstrapping } = useAuth();
  const isReaderRoute = /^\/books\/[^/]+\/read$/.test(location.pathname);

  if (isBootstrapping) {
    return null;
  }

  if (isReaderRoute) {
    return <UnifiedLoadingScreen visible progress={0} stage="Preparing reader..." />;
  }

  return (
    <div className="flex items-center justify-center min-h-64" role="status">
      <div className="animate-pulse text-gray-500">Loading...</div>
    </div>
  );
};

export const router = createBrowserRouter([
  {
    path: "/auth/callback",
    element: (
      <OAuthErrorBoundary>
        <OAuthCallback />
      </OAuthErrorBoundary>
    ),
  },
  {
    path: "/book/:bookId/read",
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <ReaderChatExperiencePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: "/onboarding",
    element: (
      <ProtectedRoute skipOnboardingCheck>
        <Suspense fallback={<LoadingFallback />}>
          <OnboardingPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: "/",
    element: <AppLayout />,
    children: [
      { index: true, element: <Home /> },
      {
        path: "login",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: "signup",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SignupPage />
          </Suspense>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "check-email",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <CheckYourEmailPage />
          </Suspense>
        ),
      },
      {
        path: "reset-password",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "password-reset-success",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PasswordResetSuccessPage />
          </Suspense>
        ),
      },
      {
        path: "verify-email",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EmailVerificationPage />
          </Suspense>
        ),
      },
      {
        path: "gallery",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <GalleryPage />
          </Suspense>
        ),
      },
      {
        path: "library",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <LibraryPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "book/:id",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <BookPage />
          </Suspense>
        ),
      },
      {
        path: "search",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SearchPage />
          </Suspense>
        ),
      },
      {
        path: "settings",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SettingsPage />
          </Suspense>
        ),
      },
      {
        path: "health",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Health />
          </Suspense>
        ),
      },
      {
        path: "privacy",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PrivacyPage />
          </Suspense>
        ),
      },
      {
        path: "terms",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EulaPage />
          </Suspense>
        ),
      },
      {
        path: "admin/books",
        element: (
          <ProtectedRoute>
            <AdminRoute>
              <Suspense fallback={<LoadingFallback />}>
                <AdminBookListPage />
              </Suspense>
            </AdminRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/book/new",
        element: (
          <ProtectedRoute>
            <AdminRoute>
              <Suspense fallback={<LoadingFallback />}>
                <NewBookIngestPage />
              </Suspense>
            </AdminRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/book/:id/edit",
        element: (
          <ProtectedRoute>
            <AdminRoute>
              <Suspense fallback={<LoadingFallback />}>
                <EditBookPage />
              </Suspense>
            </AdminRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/book/:id/graph",
        element: (
          <ProtectedRoute>
            <AdminRoute>
              <Suspense fallback={<LoadingFallback />}>
                <BookGraphPage />
              </Suspense>
            </AdminRoute>
          </ProtectedRoute>
        ),
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
